import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import { PaginationSchema } from 'types/pagination';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from '../../../shared/hooks/utils';

import { ReminderSchema } from './use-load-reminder';

const LightReminderSchema = ReminderSchema.omit({
  letters: true,
  emails: true,
  aiCall: true,
  resentVia: true,
  original: true,
  status: true,
  ignoredReason: true,
}).extend({
  failed: z.boolean(),
  automaticResent: z.boolean(),
});

export const RemindersSchema = z.object({
  data: z.array(LightReminderSchema),
  pagination: PaginationSchema,
});

export type LightReminder = z.infer<typeof LightReminderSchema>;
export type Reminders = z.infer<typeof RemindersSchema>;

export type LoadRemindersVariables = {
  page: number;
  pageLimit: number;
  reference?: string;
  startDate?: Date;
  endDate?: Date;
  order: string;
};

export async function loadRemindersQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/payment_requests', { params: queryKey[1] });

  const safeParse = RemindersSchema.safeParse(data);
  if (!safeParse.success) {
    handleZodError(safeParse, 'reminders');
    throw safeParse.error;
  }

  return safeParse.data;
}

const emptyReminders: Reminders = {
  data: [],
  pagination: {
    totalObjects: 0,
    totalPages: 0,
    pageLimit: 0,
  },
};

export const useLoadReminders = (variables: LoadRemindersVariables) => {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Reminders, ApiError>({
    queryKey: ['reminders', variables],
    queryFn: context => loadRemindersQueryFn(axiosInstance, context),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;

      return emptyReminders;
    },
  });

  return addResourceNameToQueryResult<Reminders, unknown, 'reminders', 'PreventUndefined'>(
    'reminders',
    queryResult,
    emptyReminders,
  );
};
